<script setup lang="ts">
// What can be done with one library file (HRTH-164.2), behind the ⋯ on its row: a sheet
// from the bottom on phones, a small panel on wider screens, every action a full-width
// row big enough to tap.
import { onMounted, onUnmounted } from 'vue'

export interface SheetAction {
  label: string
  /** data-testid of the action's row. */
  id: string
  run: () => unknown
  danger?: boolean
}

defineProps<{ title: string; subtitle?: string; actions: SheetAction[] }>()
const emit = defineEmits<{ close: [] }>()

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') emit('close')
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))

function pick(a: SheetAction): void {
  emit('close')
  void a.run()
}
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-end justify-center bg-black/60 md:items-center md:p-4" data-testid="action-sheet" @click.self="emit('close')">
      <div class="panel w-full border-border-strong max-md:border-x-0 max-md:border-b-0 max-md:pb-[max(0.5rem,env(safe-area-inset-bottom))] md:max-w-xs" role="menu" :aria-label="title">
        <div class="border-b border-border px-5 py-3">
          <div class="truncate text-sm" data-testid="action-sheet-title">{{ title }}</div>
          <div v-if="subtitle" class="mono truncate text-text-muted">{{ subtitle }}</div>
        </div>
        <button
          v-for="a in actions"
          :key="a.id"
          role="menuitem"
          class="flex h-12 w-full items-center px-5 text-left text-sm transition-colors hover:bg-bg-hover md:h-10"
          :class="a.danger ? 'text-error' : 'text-text-primary'"
          :data-testid="a.id"
          @click="pick(a)"
        >
          {{ a.label }}
        </button>
        <button class="mono flex h-12 w-full items-center justify-center border-t border-border text-text-muted uppercase hover:text-text-primary md:hidden" @click="emit('close')">
          Cancel
        </button>
      </div>
    </div>
  </Teleport>
</template>
